import { ContractFieldAndGrouping, ContractFieldGrouping } from "@common/components/filterPopover/FilterPopover.types.ts";
import { TableFields } from "@common/components/tableDisplayFields/TableFields.types.ts";
import { useContractInputFieldsMapping } from "@common/hooks/useSelectableItemQueries.ts";
import { ContractFieldValue } from "../Contracts.types";
import { EntityFieldType } from "@common/entityField.types";
import { mapInputFieldTypeToContractFieldType } from "../contractFieldTypeMapping.ts";
import { ContractColumn, ContractDisplayFieldRequest, getFilterIdByEnum } from "./ContractDisplayFields.types.ts";

export default function useContractDisplayFields(isEnabled: boolean = true) {
    const query = useContractInputFieldsMapping(isEnabled);

    function getTableFieldsDictionary(): Record<string, ContractFieldAndGrouping> {
        const dictionary: Record<string, ContractFieldAndGrouping> = Object.fromEntries(
            Object.entries(query.data?.contractFieldsAndGroupings ?? []).map(([key, value]) => [key, value])
        );

        // Add custom fields
        if (query.data) {
            query.data.customFieldsSelectableValues.forEach((x) => {
                dictionary[x.id] = { translation: x.name, column: undefined, grouping: ContractFieldGrouping.CustomFields };
            });
        }

        return dictionary;
    }

    function getFieldDisplayName(fieldValue: ContractFieldValue, dictionary: Record<string, ContractFieldAndGrouping>): string {
        if (fieldValue.column === ContractColumn.CounterPartiesWithSubContractors) {
            const filterId = getFilterIdByEnum(ContractColumn.ExternalContractParties, getFilterIdToEnumMap());
            return filterId ? dictionary[filterId].translation || filterId : "";
        }

        if (fieldValue.isCustomField && fieldValue.customFieldId) {
            return dictionary[fieldValue.customFieldId].translation || fieldValue.customFieldId;
        }

        if (fieldValue.column !== undefined) {
            const filterId = getFilterIdByEnum(fieldValue.column, getFilterIdToEnumMap());
            return filterId ? dictionary[filterId].translation || filterId : "";
        }

        return "";
    }

    function getTableFields(): Array<TableFields> {
        const dictionary = getTableFieldsDictionary();
        return Object.entries(dictionary).map(([id, obj]) => ({
            id,
            name: obj.translation,
            grouping: obj.grouping,
        }));
    }

    function getContractDataForExportAllData() {
        const dictionary = getTableFieldsDictionary();
        const allFieldKeys: Array<string> = Object.keys(dictionary);

        const request = new ContractDisplayFieldRequest(allFieldKeys, getFilterIdToEnumMap());

        const tableFields: Array<TableFields> = Object.entries(dictionary).map(([id, obj]) => ({
            id,
            name: obj.translation,
            grouping: obj.grouping,
        }));
        return { tableFields, contractFieldRequest: { displayFields: request } };
    }

    function getCustomFieldType(customFieldId: string): EntityFieldType | undefined {
        const customField = query.data?.customFieldsSelectableValues.find((x) => x.id === customFieldId);
        if (!customField) return undefined;
        return mapInputFieldTypeToContractFieldType(customField.type);
    }

    function getFilterIdToEnumMap(): Record<string, ContractColumn | undefined> {
        return Object.fromEntries(Object.entries(query.data?.contractFieldsAndGroupings ?? []).map(([key, value]) => [key, value.column]));
    }

    return {
        isLoading: query.isLoading,
        tableFields: getTableFields(),
        getTableFieldsDictionary,
        getFieldDisplayName,
        getCustomFieldType,
        getContractDataForExportAllData,
        filterIdToEnumMap: getFilterIdToEnumMap(),
    };
}
